/**
 * EONDiffuse — the ENERGY-PRESERVING OREN–NAYAR rough diffuse BRDF as an opt-in lighting model for three r185
 * (shader gaps program round 4, material models, 2026-09-30).
 *
 * Jamie Portsmouth, Peter Kutz, Stephen Hill, "EON: A Practical Energy-Preserving Rough Diffuse BRDF", JCGT 14(1),
 * 2025 (https://jcgt.org/published/0014/01/06/, arXiv 2410.18026 v3 of 2025-12-29). The evaluation below is a port of
 * the paper's Listing 1 / the reference `main.glsl` of https://github.com/portsmouth/EON-diffuse —
 *   MIT License, Copyright (c) 2024 Jamie Portsmouth. Permission is hereby granted, free of charge, to any person
 *   obtaining a copy of this software … to deal in the Software without restriction … subject to the following
 *   conditions: The above copyright notice and this permission notice shall be included in all copies or substantial
 *   portions of the Software. THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND.
 * three.js adopted the same model for r187 (`BRDF_EON.js`, PR #34660 — the directional albedo shared by BOTH indirect
 * paths); this is the r185 back-port of that design, written against r185's `PhysicalLightingModel`.
 *
 * THE MODEL. Fujii's Oren–Nayar (FON) single scattering
 *     f_ss = ρ/π · A_F · (1 + r · s/t),  s = ωi·ωo − (n·ωi)(n·ωo),  s/t = s / max(n·ωi, n·ωo) if s > 0 else s,
 *     A_F = 1 / (1 + (½ − 2/(3π)) r)
 * plus Kulla–Conty-style energy compensation built on the FON directional albedo E_F(μ) (the paper's 4-term cubic in
 * 1 − μ is the shader form; the exact form is the CPU oracle):
 *     f_ms = ρ_ms/π · (1 − E_F(μi)) (1 − E_F(μo)) / (1 − ⟨E_F⟩),   ρ_ms = ρ² ⟨E_F⟩ / (1 − ρ (1 − ⟨E_F⟩))
 * so a white (ρ = 1) surface reflects EXACTLY its irradiance at every roughness: the directional albedo
 *     E_EON(ρ, μ) = ρ E_F(μ) + ρ_ms (1 − E_F(μ))   is 1 for ρ = 1.
 * r is the DIFFUSE roughness ∈ [0, 1] (not the specular roughness); r = 0 is Lambert exactly.
 *
 * WHAT r185 LACKS, AND HOW THIS ADDS IT (the smallest override that swaps only the diffuse):
 *   • `start()` reassigns three's `DiffuseContribution` property (setupVariants set it to ρ·(1 − metal)) to the EON
 *     DIRECTIONAL ALBEDO E_EON(ρ, r, n·v)·(1 − metal). Every INDIRECT path reads that property — `indirectDiffuse`
 *     (probes / hemisphere: E/π — three r187's form) and `indirectSpecular` (the environment's diffuse share under the
 *     dielectric Fresnel) — so the environment response is the matched EON albedo, not Lambert's ρ.
 *   • `direct()` lets three add its diffuse (irradiance · DiffuseContribution/π) and then adds the difference to the
 *     EON lobe: irradiance · (f_EON·(1 − metal) − DiffuseContribution/π) — the net is the EON BRDF. Specular, shadows,
 *     clear coat etc. are untouched. (Rect area lights keep three's LTC diffuse over the matched albedo — a documented
 *     approximation.)
 * COLOUR. `colorMode: 'white'` (the default — the paper's §3 alternative "evaluate with ρ = 1 and multiply by the
 * artist colour") keeps every albedo EXACTLY where the Lambert look put it (E_EON(1) = 1: the ambient response is
 * Lambert's; only the direct light's angular spread changes — the flattening and the back-scatter). 'physical' uses
 * ρ = the colour (the multiple-scattering saturation of a rough coloured surface — a slight colour shift).
 * NOT for MeshPhysical materials with sheen (three scales the irradiance by the sheen's energy compensation first) —
 * `applyEONDiffuse` refuses those and keeps Lambert.
 *
 * THE CLASSIC-KEY LAW: the lighting model is not a node property, so `applyEONDiffuse` also sets an own node property
 * (`diffuseRoughnessNode`) — a NodeMaterial keys every own node property, and Atlas's `keyClassicNodeGraph` keys every
 * `*Node` property of a classic material; three's `NodeLibrary.fromMaterial` copies both it and `setupLightingModel`.
 */
import * as THREE from 'three/webgpu';
import {
  float, vec3, uniform, dot, max, select, diffuseColor, metalness, normalView, positionViewDirection,
} from 'three/tsl';

type N = any;
type RGB = [number, number, number];

export const EON_CONSTANT1_FON = 0.5 - 2 / (3 * Math.PI);
export const EON_CONSTANT2_FON = 2 / 3 - 28 / (15 * Math.PI);
const EON_EPS = 1e-7;
const G1 = 0.0571085289, G2 = 0.491881867, G3 = -0.332181442, G4 = 0.0714429953;

// ─── CPU oracles (tests + the Atlas harness) ────────────────────────────────────────────────────────────────────

/** FON directional albedo, analytic (the reference repo's numerically stable form). */
export function eFonExactCpu(mu: number, r: number): number {
  const AF = 1 / (1 + EON_CONSTANT1_FON * r), BF = r * AF;
  const m = Math.min(1, Math.max(-1, mu));
  const Si = Math.sqrt(1 - m * m);
  const G = Si * (Math.acos(m) - Si * m) + (2 / 3) * (Si * m * (1 + Si + Si * Si) / (1 + Si) - Si);
  return AF + (BF / Math.PI) * G;
}
/** FON directional albedo, the paper's cubic fit (< 0.1 % from exact) — what the shader evaluates. */
export function eFonApproxCpu(mu: number, r: number): number {
  const mc = 1 - mu;
  const gOverPi = mc * (G1 + mc * (G2 + mc * (G3 + mc * G4)));
  return (1 + r * gOverPi) / (1 + EON_CONSTANT1_FON * r);
}
/** The EON BRDF value (per channel), directions in a frame where +z is the normal. `exact` picks the albedo form. */
export function fEonCpu(rho: RGB, r: number, wi: RGB, wo: RGB, exact = false): RGB {
  if (r <= EON_EPS) return rho.map((p) => p / Math.PI) as RGB; // the shader's guard (three r187's too): Lambert exactly
  const mi = wi[2], mo = wo[2];
  const s = wi[0] * wo[0] + wi[1] * wo[1] + wi[2] * wo[2] - mi * mo;
  const sOverT = s > 0 ? s / Math.max(mi, mo) : s;
  const AF = 1 / (1 + EON_CONSTANT1_FON * r);
  const EFo = exact ? eFonExactCpu(mo, r) : eFonApproxCpu(mo, r);
  const EFi = exact ? eFonExactCpu(mi, r) : eFonApproxCpu(mi, r);
  const avgEF = AF * (1 + EON_CONSTANT2_FON * r);
  return rho.map((p) => {
    const fss = (p / Math.PI) * AF * (1 + r * sOverT);
    const rhoMs = (p * p) * avgEF / (1 - p * (1 - avgEF));
    const fms = (rhoMs / Math.PI) * Math.max(EON_EPS, 1 - EFo) * Math.max(EON_EPS, 1 - EFi) / Math.max(EON_EPS, 1 - avgEF);
    return fss + fms;
  }) as RGB;
}
/** The EON directional albedo E_EON(ρ, r, μ) (Listing 1 `E_EON`). */
export function eEonCpu(rho: RGB, r: number, mu: number, exact = false): RGB {
  const AF = 1 / (1 + EON_CONSTANT1_FON * r);
  const EF = exact ? eFonExactCpu(mu, r) : eFonApproxCpu(mu, r);
  const avgEF = AF * (1 + EON_CONSTANT2_FON * r);
  return rho.map((p) => p * EF + ((p * p) * avgEF / (1 - p * (1 - avgEF))) * (1 - EF)) as RGB;
}
/**
 * The directional albedo by quadrature: ∫ f_EON(ωi, ωo) cos θi dωi over the hemisphere (midpoint rule in θ, φ).
 * The white-furnace oracle: ≤ 1 for ρ ≤ 1, and = E_EON within the quadrature error.
 */
export function eonAlbedoQuadratureCpu(rho: RGB, r: number, thetaO: number, nTheta = 96, nPhi = 96, exact = true): RGB {
  const wo: RGB = [Math.sin(thetaO), 0, Math.cos(thetaO)];
  const acc: RGB = [0, 0, 0];
  const dT = (Math.PI / 2) / nTheta, dP = (2 * Math.PI) / nPhi;
  for (let i = 0; i < nTheta; i++) {
    const t = (i + 0.5) * dT, st = Math.sin(t), ct = Math.cos(t);
    for (let j = 0; j < nPhi; j++) {
      const p = (j + 0.5) * dP;
      const f = fEonCpu(rho, r, [st * Math.cos(p), st * Math.sin(p), ct], wo, exact);
      const w = ct * st * dT * dP;
      acc[0] += f[0] * w; acc[1] += f[1] * w; acc[2] += f[2] * w;
    }
  }
  return acc;
}

// ─── TSL ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/** FON directional albedo (the cubic fit), given A_F. */
function fonAlbedoNode(mu: N, r: N, A: N): N {
  const mc: N = float(1).sub(mu);
  const gOverPi: N = mc.mul(mc.mul(mc.mul(mc.mul(G4).add(G3)).add(G2)).add(G1));
  return A.mul(r.mul(gOverPi).add(1));
}
/** A_F and ⟨E_F⟩ for a roughness node. */
function fonCoefficients(r: N): { A: N; avg: N } {
  const A: N = r.mul(EON_CONSTANT1_FON).add(1).reciprocal();
  return { A, avg: A.mul(r.mul(EON_CONSTANT2_FON).add(1)) };
}
/** ρ_ms = ρ² ⟨E_F⟩ / (1 − ρ(1 − ⟨E_F⟩)) — guarded. */
function rhoMsNode(rho: N, avg: N): N {
  return rho.mul(rho).mul(avg).div(max(float(1).sub(rho.mul(float(1).sub(avg))), float(EON_EPS)));
}

/**
 * The EON BRDF (per channel) for a light direction L and view V around the normal N (all view space, unit length).
 * r = 0 selects Lambert ρ/π exactly (the paper's limit, and three r187's guard).
 */
export function brdfEonNode(o: { lightDirection: N; diffuseColor: N; roughness: N; normal?: N; viewDirection?: N }): N {
  const n: N = o.normal ?? normalView, v: N = o.viewDirection ?? positionViewDirection;
  const r: N = float(o.roughness as never), rho: N = vec3(o.diffuseColor as never).clamp(0, 1);
  const dotNL: N = dot(n, o.lightDirection).clamp(0, 1), dotNV: N = dot(n, v).clamp(0, 1);
  const s: N = dot(o.lightDirection, v).sub(dotNL.mul(dotNV));
  const sOverT: N = select(s.greaterThan(0), s.div(max(max(dotNL, dotNV), float(EON_EPS))), s);
  const { A, avg } = fonCoefficients(r);
  const fss: N = rho.mul(A.mul(1 / Math.PI)).mul(r.mul(sOverT).add(1));
  const eV: N = fonAlbedoNode(dotNV, r, A), eL: N = fonAlbedoNode(dotNL, r, A);
  const fms: N = rhoMsNode(rho, avg).mul(1 / Math.PI)
    .mul(max(float(1).sub(eV), float(EON_EPS)).mul(max(float(1).sub(eL), float(EON_EPS))))
    .div(max(float(1).sub(avg), float(EON_EPS)));
  return select(r.lessThanEqual(EON_EPS), rho.mul(1 / Math.PI), fss.add(fms));
}

/** The EON directional albedo E_EON(ρ, r, μ) — the matched environment response. r = 0 ⇒ ρ. */
export function eonDirectionalAlbedoNode(diffuse: N, roughness: N, dotNV: N): N {
  const r: N = float(roughness as never), rho: N = vec3(diffuse as never).clamp(0, 1);
  const { A, avg } = fonCoefficients(r);
  const e: N = fonAlbedoNode(float(dotNV as never).clamp(0, 1), r, A);
  return select(r.lessThanEqual(EON_EPS), rho, rho.mul(e).add(rhoMsNode(rho, avg).mul(float(1).sub(e))));
}

// ─── the lighting model ────────────────────────────────────────────────────────────────────────────────────────

/**
 * An extra DIRECT term a material model adds (e.g. `FlakeGlints`): `start` runs once at the top of the lighting stack
 * (uniform control flow — the place for derivatives: THE LAZY-VAR LAW), `direct` per light after three's own direct().
 */
export interface DirectHook {
  start?(builder: N): void;
  direct(light: { lightDirection: N; lightColor: N; reflectedLight: N }, builder: N): void;
}

export interface EONLightingOptions {
  /** The diffuse roughness node (0 = Lambert); null = no EON (the model only carries direct hooks). */
  roughness: N | null;
  colorMode?: 'white' | 'physical';
  directHooks?: DirectHook[];
}

/** three's `DiffuseContribution` property (r185; not in the `three/tsl` bundle's name list — read off the real TSL namespace export). */
function diffuseContributionNode(): N | null {
  const tsl = (THREE as unknown as { TSL?: Record<string, unknown> }).TSL;
  return (tsl?.diffuseContribution as N) ?? null;
}

export const eonStats = { models: 0, applied: 0, refused: 0 };

/**
 * `PhysicalLightingModel` with the diffuse swapped for EON (see the header) and optional extra direct hooks. For
 * MeshStandard(Node)Material — the constructor's feature flags are all off (clear coat, sheen … stay Lambert-safe).
 */
export class EONLightingModel extends THREE.PhysicalLightingModel {
  private readonly eonR: N | null;
  private readonly mode: 'white' | 'physical';
  private readonly hooks: DirectHook[];
  private dc: N | null = null;
  constructor(opts: EONLightingOptions) {
    super();
    this.eonR = opts.roughness;
    this.mode = opts.colorMode ?? 'white';
    this.hooks = opts.directHooks ?? [];
    eonStats.models++;
  }
  /** ρ for the EON evaluation and the factor it is multiplied by afterwards. */
  private rhoAndTint(): { rho: N; tint: N } {
    return this.mode === 'white'
      ? { rho: vec3(1, 1, 1), tint: vec3(diffuseColor.rgb).mul(float(1).sub(metalness)) }
      : { rho: vec3(diffuseColor.rgb), tint: float(1).sub(metalness) };
  }
  start(builder: any): void {
    for (const h of this.hooks) h.start?.(builder);
    this.dc = this.eonR ? diffuseContributionNode() : null;
    if (this.eonR && this.dc) {
      const dotNV: N = dot(normalView, positionViewDirection).clamp(0, 1);
      const { rho, tint } = this.rhoAndTint();
      // the matched environment response: every indirect path of r185 reads DiffuseContribution
      this.dc.assign(eonDirectionalAlbedoNode(rho, this.eonR, dotNV).mul(tint));
    }
    super.start(builder);
  }
  direct(light: any, builder: any): void {
    super.direct(light, builder);
    if (this.eonR && this.dc) {
      const irradiance: N = dot(normalView, light.lightDirection).clamp(0, 1).mul(light.lightColor);
      const { rho, tint } = this.rhoAndTint();
      const eon: N = brdfEonNode({ lightDirection: light.lightDirection, diffuseColor: rho, roughness: this.eonR }).mul(tint);
      // three added irradiance · DiffuseContribution/π (the matched albedo, Lambert-shaped); this makes the net the EON lobe
      light.reflectedLight.directDiffuse.addAssign(irradiance.mul(eon.sub(this.dc.mul(1 / Math.PI))));
    }
    for (const h of this.hooks) h.direct(light, builder);
  }
}

export interface EONDiffuseHandle {
  /** The diffuse roughness uniform (live). */
  roughness: { value: number };
  applied: boolean;
}

/**
 * Give a MeshStandard(Node)Material the EON diffuse with its own `roughness` (0..1, live). Composes with direct hooks
 * installed before or after (`addDirectHook`). Refuses physical materials with sheen / transmission (they keep Lambert).
 */
export function applyEONDiffuse(material: THREE.Material, opts: { roughness: number; colorMode?: 'white' | 'physical' }): EONDiffuseHandle {
  const m = material as unknown as Record<string, unknown> & { userData: Record<string, unknown>; needsUpdate: boolean; isMeshStandardMaterial?: boolean; isMeshStandardNodeMaterial?: boolean; sheen?: number; transmission?: number };
  const u = uniform(opts.roughness);
  if (!(m.isMeshStandardMaterial || m.isMeshStandardNodeMaterial) || (m.sheen ?? 0) > 0 || (m.transmission ?? 0) > 0) {
    eonStats.refused++;
    return { roughness: u as unknown as { value: number }, applied: false };
  }
  const spec = lightingSpecOf(m);
  spec.roughness = u;
  spec.colorMode = opts.colorMode ?? 'white';
  m.diffuseRoughnessNode = u; // THE CLASSIC-KEY LAW: an own node property puts the EON variant in the program key
  installLighting(m, spec);
  eonStats.applied++;
  return { roughness: u as unknown as { value: number }, applied: true };
}

/** Add an extra direct term (a material model's lobe) to a material, composing with an EON diffuse if it has one. */
export function addDirectHook(material: THREE.Material, makeHook: () => DirectHook, keyNode: N): void {
  const m = material as unknown as Record<string, unknown> & { userData: Record<string, unknown>; needsUpdate: boolean };
  const spec = lightingSpecOf(m);
  spec.hooks.push(makeHook);
  m[`materialModelHook${spec.hooks.length}Node`] = keyNode; // in the program key (see applyEONDiffuse)
  installLighting(m, spec);
}

interface LightingSpec { roughness: N | null; colorMode: 'white' | 'physical'; hooks: Array<() => DirectHook> }
const SPEC = new WeakMap<object, LightingSpec>();
function lightingSpecOf(m: object): LightingSpec {
  let s = SPEC.get(m);
  if (!s) { s = { roughness: null, colorMode: 'white', hooks: [] }; SPEC.set(m, s); }
  return s;
}
function installLighting(m: Record<string, unknown> & { needsUpdate: boolean }, spec: LightingSpec): void {
  // an OWN property: three's NodeLibrary.fromMaterial copies it onto a classic material's node twin (the ground's pattern)
  // a FRESH hook per build (a hook keeps the vars its start() made for the direct() calls of that build)
  m.setupLightingModel = () => new EONLightingModel({ roughness: spec.roughness, colorMode: spec.colorMode, directHooks: spec.hooks.map((f) => f()) });
  m.needsUpdate = true;
}

/** Whether a material wears the EON diffuse (for levers / harnesses). */
export function hasEONDiffuse(material: THREE.Material): boolean {
  return !!SPEC.get(material)?.roughness;
}

